import type { MenuLink } from '@iheartjane/data-access';
import type { SelectFieldProps } from '@iheartjane/reefer';
import {
  AddIcon,
  Box,
  Button,
  ChevronDownIcon,
  Flex,
  Popover,
  TrashIcon,
  Typography,
} from '@iheartjane/reefer';
import { Form } from '@iheartjane/reefer-hook-form';
import { clsx } from 'clsx';
import isEmpty from 'lodash-es/isEmpty';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import type {
  ControllerRenderProps,
  FieldArrayPath,
  FieldError,
  FieldValues,
  Path,
  UseFormReturn,
} from 'react-hook-form';

import { UrlEmailPhoneInput } from '@jane/business-admin/components/premium/UrlEmailPhoneInput';
import { UrlInput } from '@jane/business-admin/components/premium/UrlInput';
import { ConfirmChangeModal, SortableList } from '@jane/shared/components';

import { MaxCharacterHelper } from './MaxCharacterHelper';

import styles from './PremiumLinks.module.css';

export interface PremiumLinksProps<TFieldValues extends FieldValues> {
  addButtonLabel: string;
  allowEmailAndPhone?: boolean;
  controlName: FieldArrayPath<TFieldValues>;
  formMethods: UseFormReturn<TFieldValues>;
  labelOptions?: SelectFieldProps['options'];
  /** Max number of children per parent */
  maxChildren?: number;
  /** Max depth of nested links */
  maxDepth?: number;
  /** Max length of labels */
  maxLabelLength?: number;
  /** Max number of parent links */
  maxParents?: number;
}

interface PremiumLinkItemsProps<TFieldValues extends FieldValues> extends Omit<
  PremiumLinksProps<TFieldValues>,
  'addButtonLabel'
> {
  /** For internal use, current nest level - 0 indexed */
  nestLevel?: number;
  /** For internal use, keep track of the parent indexes */
  parentIndexes?: number[];
}

/**
 * Button to add children to a parent link.
 * Disables button and shows a tooltip when the maximum number of children is reached.
 */
const AddChildButton = ({
  addChild,
  maxChildren,
  reachedMaxChildren,
}: {
  addChild: () => void;
  maxChildren?: number;
  reachedMaxChildren: boolean;
}) => {
  return (
    <Box ml={24} mr={24} mt={12}>
      {maxChildren && reachedMaxChildren ? (
        <Popover
          label="Add sub-menu item"
          openOn="hover"
          alignment={{ horizontal: 'right', vertical: 'top' }}
          target={
            <Button.Icon
              className={styles['addSubItemButton']}
              disabled
              icon={<AddIcon size="sm" />}
              label="Add sub-menu item"
              variant="primary-inverse"
            />
          }
        >
          <Typography p={24} width="285px">
            Each section can have {maxChildren} links max
          </Typography>
        </Popover>
      ) : (
        <Button.Icon
          className={styles['addSubItemButton']}
          icon={<AddIcon size="sm" />}
          label="Add sub-menu item"
          onClick={addChild}
          variant="primary-inverse"
        />
      )}
    </Box>
  );
};

/**
 * Label field for a link
 * - Shows a chevron down icon if the link has children.
 * - Shows a helper text with the maximum number of characters allowed.
 * - Shows a select field with the options if labelOptions are provided.
 */
const LabelField = <TFieldValues extends FieldValues>({
  error,
  field,
  hasSubItems,
  labelOptions,
  maxLabelLength,
}: {
  error?: FieldError;
  field: ControllerRenderProps<TFieldValues, Path<TFieldValues>>;
  hasSubItems?: boolean;
  labelOptions?: SelectFieldProps['options'];
  maxLabelLength?: number;
}) => {
  return (
    <Box
      width="150px"
      className={clsx({
        [styles['fieldError']]: error && !isEmpty(error),
      })}
    >
      {labelOptions ? (
        <Form.SelectField
          className={styles['selectLabelField']}
          options={labelOptions}
          defaultValue={field.value}
          placeholder="Select"
          label=""
          labelHidden
          {...field}
        />
      ) : (
        <Form.TextField
          className={styles['labelField']}
          startIcon={
            hasSubItems ? (
              <ChevronDownIcon color="purple" size="sm" mr={12} />
            ) : null
          }
          label=""
          labelHidden
          maxLength={maxLabelLength}
          defaultValue={field.value}
          placeholder="Add label"
          {...field}
          helperText={
            maxLabelLength ? (
              <MaxCharacterHelper
                value={field.value}
                maxLength={maxLabelLength}
                mt={8}
              />
            ) : null
          }
        />
      )}
    </Box>
  );
};

const PremiumLinkItems = <TFieldValues extends FieldValues>({
  allowEmailAndPhone,
  controlName,
  formMethods,
  itemsToAdd,
  labelOptions,
  maxChildren,
  maxLabelLength,
  maxDepth = 2,
  nestLevel = 0,
  parentIndexes = [],
  setItemsToAdd,
}: PremiumLinkItemsProps<TFieldValues> & {
  itemsToAdd: number[] | null;
  setItemsToAdd: Dispatch<SetStateAction<number[] | null>>;
}) => {
  const [itemToDelete, setItemToDelete] = useState<number | null>(null);
  const name =
    nestLevel === 0
      ? controlName
      : (`${controlName}.${parentIndexes.join(
          '.links.'
        )}.links` as FieldArrayPath<TFieldValues>);

  const { fields, append, remove, move } = useFieldArray<TFieldValues>({
    control: formMethods.control,
    name,
  });
  const items = formMethods.watch(name as Path<TFieldValues>) as
    MenuLink[] | undefined;

  const confirmDeleteItem = useCallback(
    (index: number) => {
      const item = items?.[index];
      if (!item) return;
      if (item.destination || item.label) {
        setItemToDelete(index);
      } else {
        remove(index);
      }
    },
    [items, remove]
  );

  const canAddChildren = nestLevel < maxDepth;

  useEffect(() => {
    if (itemsToAdd === null) return;

    let isMatch = false;
    if (itemsToAdd.length === parentIndexes.length) {
      if (itemsToAdd.length === 0) {
        // Special case for top level
        isMatch = true;
      } else {
        // Compare arrays element by element
        isMatch = itemsToAdd.every(
          (val, index) => val === parentIndexes[index]
        );
      }
    }

    if (isMatch) {
      append({ label: '', destination: '', links: [] } as Parameters<
        typeof append
      >[0]);
      setItemsToAdd(null);
    }
  }, [itemsToAdd, append, setItemsToAdd, parentIndexes, name]);

  return (
    <Flex
      className={styles['linksWrapper']}
      flexDirection="column"
      gap={8}
      pt={nestLevel > 0 && fields.length > 0 ? 24 : 0}
    >
      <SortableList
        className={styles['sortableList']}
        direction="vertical"
        items={fields}
        onChange={(_, oldIndex, newIndex) => {
          move(oldIndex, newIndex);
        }}
        variableHeights
        renderItem={(item, idx) => {
          const currentItemValue = ((formMethods.watch(
            name as Path<TFieldValues>
          ) as MenuLink[] | undefined) ?? [])[idx];

          const itemCanHaveChildren =
            currentItemValue &&
            typeof currentItemValue === 'object' &&
            'links' in currentItemValue;

          const hasSubItems =
            itemCanHaveChildren &&
            Array.isArray(currentItemValue.links) &&
            currentItemValue.links.length > 0;

          const reachedMaxChildren =
            (currentItemValue?.links?.length ?? 0) >= (maxChildren ?? Infinity);

          return (
            <div key={item.id}>
              <Flex>
                <Controller
                  name={`${name}.${idx}.label` as Path<TFieldValues>}
                  control={formMethods.control}
                  rules={{
                    required: true,
                    pattern: /\S+/,
                  }}
                  render={({ field, fieldState: { error } }) => (
                    <LabelField
                      error={error}
                      field={field}
                      hasSubItems={hasSubItems}
                      labelOptions={labelOptions}
                      maxLabelLength={maxLabelLength}
                    />
                  )}
                />
                <Controller
                  name={`${name}.${idx}.destination` as Path<TFieldValues>}
                  control={formMethods.control}
                  rules={{
                    required: !hasSubItems,
                  }}
                  render={({ field, fieldState: { error } }) => {
                    // Re-validate if the field is now optional but still has a stale required error
                    if (hasSubItems && error?.type === 'required') {
                      formMethods.trigger(field.name);
                    }
                    return (
                      <Box
                        width="100%"
                        mr={canAddChildren ? 0 : nestLevel === 0 ? 24 : 72}
                        className={clsx({
                          [styles['fieldError']]: error && !isEmpty(error),
                        })}
                      >
                        {allowEmailAndPhone ? (
                          <UrlEmailPhoneInput<TFieldValues>
                            field={field}
                            placeholder={`Add URL${canAddChildren ? ' (optional)' : ''}`}
                          />
                        ) : (
                          <UrlInput<TFieldValues>
                            field={field}
                            placeholder={`Add URL${canAddChildren ? ' (optional)' : ''}`}
                          />
                        )}
                      </Box>
                    );
                  }}
                />
                {canAddChildren && (
                  <AddChildButton
                    addChild={() =>
                      setItemsToAdd(
                        nestLevel === 0 ? [idx] : [...parentIndexes, idx]
                      )
                    }
                    maxChildren={maxChildren}
                    reachedMaxChildren={reachedMaxChildren}
                  />
                )}
                <TrashIcon
                  altText={`Remove sub-item ${parentIndexes[nestLevel] + 1}:${
                    idx + 1
                  }`}
                  size="lg"
                  color="grays-mid"
                  onClick={() => confirmDeleteItem(idx)}
                  mt={8}
                />
              </Flex>
              <PremiumLinkItems
                allowEmailAndPhone={allowEmailAndPhone}
                parentIndexes={[...parentIndexes, idx]}
                formMethods={formMethods}
                controlName={controlName}
                itemsToAdd={itemsToAdd}
                maxDepth={maxDepth}
                maxLabelLength={maxLabelLength}
                maxChildren={maxChildren}
                nestLevel={nestLevel + 1}
                setItemsToAdd={setItemsToAdd}
              />
            </div>
          );
        }}
      />

      {itemToDelete !== null && (
        <ConfirmChangeModal
          open
          title={
            <Typography variant="title-bold" mb={8}>
              Are you sure?
            </Typography>
          }
          onConfirm={() => {
            remove(itemToDelete);
            setItemToDelete(null);
          }}
          onCancel={() => setItemToDelete(null)}
          confirmButtonVariant="destructive"
          confirmLabel="Yes, remove"
          cancelLabel="No, go back"
          subtitle="Removing this row cannot be undone."
          minWidth="432px"
          borderRadius="40px"
        />
      )}
    </Flex>
  );
};

export const PremiumLinks = <TFieldValues extends FieldValues>(
  props: PremiumLinksProps<TFieldValues>
) => {
  const {
    addButtonLabel,
    allowEmailAndPhone,
    controlName,
    formMethods,
    maxParents,
  } = props;
  const [itemsToAdd, setItemsToAdd] = useState<number[] | null>(null);
  const items = formMethods.watch(controlName as Path<TFieldValues>) as
    MenuLink[] | undefined;

  const canAddItem = maxParents ? (items?.length ?? 0) < maxParents : true;

  return (
    <div className="premium-links">
      <PremiumLinkItems
        {...props}
        allowEmailAndPhone={allowEmailAndPhone}
        itemsToAdd={itemsToAdd}
        setItemsToAdd={setItemsToAdd}
      />
      <Flex
        className={styles['addLinkButton']}
        alignItems="center"
        justifyContent="center"
        gap={8}
        pt={16}
        pb={16}
        mt={24}
        ariaLabel={addButtonLabel}
        {...(canAddItem && { onClick: () => setItemsToAdd([]) })}
      >
        {' '}
        {canAddItem ? (
          <>
            <AddIcon size="sm" />
            <Typography variant="body-bold">{addButtonLabel}</Typography>
          </>
        ) : (
          <Typography variant="body-bold" color="grays-mid">
            You've reached the maximum of {maxParents} link sections
          </Typography>
        )}
      </Flex>
    </div>
  );
};
